/**
 * Status views: 404, a recoverable render error, and the preview gate.
 */
import { el } from "../core/dom.js";
import type { AppContext } from "../core/context.js";
import { applyPageSeo } from "../core/seo.js";

export function notFoundView(ctx: AppContext): HTMLElement {
	const copy = ctx.copy();
	applyPageSeo(ctx, {
		title: `${copy.status.notFoundTitle} — ${copy.brand.name}`,
		description: copy.status.notFoundDesc,
		noindex: true,
	});

	return el(
		"div",
		{ class: "mb-view mb-view--status" },
		el(
			"section",
			{ class: "mb-section mb-section--glow" },
			el(
				"div",
				{ class: "mb-section__inner mb-status" },
				el("p", { class: "mb-status__code" }, "404"),
				el("h1", { class: "mb-status__title" }, copy.status.notFoundTitle),
				el("p", { class: "mb-status__desc" }, copy.status.notFoundDesc),
				el(
					"div",
					{ class: "mb-status__actions" },
					el(
						"a",
						{
							class: "mb-cta mb-cta--accent",
							href: ctx.href("services"),
							"data-routekey": "services",
						},
						copy.status.notFoundCta,
					),
					el(
						"a",
						{ class: "mb-cta mb-cta--outline", href: ctx.href("home") },
						ctx.t("common.backHome"),
					),
				),
			),
		),
	);
}

export function errorView(ctx: AppContext, retry: () => void): HTMLElement {
	const copy = ctx.copy();
	return el(
		"div",
		{ class: "mb-view mb-view--status" },
		el(
			"section",
			{ class: "mb-section mb-section--glow" },
			el(
				"div",
				{ class: "mb-section__inner mb-status", role: "alert" },
				el("h1", { class: "mb-status__title" }, copy.status.errorTitle),
				el("p", { class: "mb-status__desc" }, copy.status.errorDesc),
				el(
					"button",
					{ class: "mb-cta mb-cta--accent", type: "button", onClick: retry },
					copy.status.retry,
				),
			),
		),
	);
}

/** A /podglad status page: never indexed, whatever it says. */
function previewStatus(
	ctx: AppContext,
	title: string,
	desc: string,
	action?: HTMLElement,
): HTMLElement {
	applyPageSeo(ctx, { title, description: "", noindex: true });

	return el(
		"div",
		{ class: "mb-view mb-view--status" },
		el(
			"section",
			{ class: "mb-section mb-section--glow" },
			el(
				"div",
				{ class: "mb-section__inner mb-status" },
				el("h1", { class: "mb-status__title" }, title),
				el("p", { class: "mb-status__desc" }, desc),
				action,
			),
		),
	);
}

/**
 * Shown when a /podglad URL is opened signed out, or the gateway refused the
 * session. Sign-in lives in the panel (route "panel", which sends the browser
 * to the panel's own host when VITE_PANEL_URL is set); its session cookie
 * covers this host too.
 */
export function previewUnauthorizedView(ctx: AppContext): HTMLElement {
	const copy = ctx.copy();
	return previewStatus(
		ctx,
		copy.preview.title,
		copy.preview.desc,
		el(
			"button",
			{
				class: "mb-cta mb-cta--accent",
				type: "button",
				onClick: () =>
					ctx.navigate("panel", undefined, {
						next: window.location.pathname + window.location.search,
					}),
			},
			copy.preview.signIn,
		),
	);
}

/** Signed in, but without a role that may read drafts: signing in again would not help. */
export function previewForbiddenView(ctx: AppContext): HTMLElement {
	const copy = ctx.copy();
	return previewStatus(ctx, copy.preview.title, copy.preview.forbidden);
}
